import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MaterialsCards } from "@/pages/MaterialsCatalogue";
import { ProductRail } from "@/components/ProductCard";
import DeliveryWorldMap from "@/components/DeliveryWorldMap";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import JustAdded from "@/components/JustAdded";
import DealOfDay from "@/components/DealOfDay";
import BecauseYouViewed from "@/components/BecauseYouViewed";
import ShopBySubCategory from "@/components/ShopBySubCategory";
import ShopByDepartment from "@/components/ShopByDepartment";
import { getCatViews } from "@/lib/recentlyViewed";
import { jmd, timeLeft } from "@/lib/format";
import {
  Cpu, Shirt, Sofa, ShoppingBasket, Wrench, Car, Palmtree, Briefcase, Handshake, Sparkles,
  ArrowRight, ChevronLeft, ChevronRight, Truck, ShieldCheck, Clock, BadgeCheck, Zap, Hotel, Smartphone,
  Droplets, Utensils, ShowerHead, Drill, Hammer, PaintRoller, Lock, DoorOpen, Plug, Waves, Trees,
  Cigarette, GlassWater, Palette, Footprints, Gem, ShoppingBag, Anchor, Sailboat, PawPrint,
} from "lucide-react";

const ICONS = { Cpu, Shirt, Sofa, ShoppingBasket, Wrench, Car, Palmtree, Briefcase, Handshake, Sparkles,
  Cigarette, GlassWater, Palette, Footprints, Gem, ShoppingBag, Anchor, Sailboat, PawPrint, Lock };
const HW_ICONS = { Droplets, Utensils, ShowerHead, Drill, Hammer, PaintRoller, Lock, DoorOpen, Plug, Waves, Trees, Wrench };

const SLIDES = [
  { tag: "Everything Jamaica", title: "Everything Jamaica.\nOne SHOP NOW.", sub: "Shop products, book hotels, discover local brands — with lightning-fast delivery from Kingston to the world.", cta: "Start Shopping", to: "/search", img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1400", accent: "emerald" },
  { tag: "Worldwide Stays", title: "From the Caribbean\nto the World.", sub: "Book boutique hotels, villas and beachfront resorts across the Caribbean and beyond — SHOP NOW delivers worldwide.", cta: "Explore Hotels", to: "/hotels", img: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?w=1400", accent: "gold" },
  { tag: "Jamaica Made", title: "Proudly Made\nin Jamaica.", sub: "Blue Mountain coffee, artisan crafts and island brands — supporting local entrepreneurs.", cta: "Shop Jamaica Made", to: "/jamaica-made", img: "https://images.unsplash.com/photo-1559056199-641a0ac8b55e?w=1400", accent: "coral" },
];

function Hero() {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((v) => (v + 1) % SLIDES.length), 6000); return () => clearInterval(t); }, []);
  const s = SLIDES[i];
  return (
    <section className="relative sn-hero-mesh sn-noise overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center relative">
        <div key={i} className="sn-fade-up text-white">
          <span className="inline-flex items-center gap-2 sn-glass sn-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
            <Sparkles size={14} className="text-emerald" /> {s.tag}
          </span>
          <h1 className="font-clash font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-5 whitespace-pre-line tracking-tight">{s.title}</h1>
          <p className="text-white/70 text-base sm:text-lg mt-5 max-w-md">{s.sub}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={s.to} className="inline-flex items-center gap-2 bg-emerald text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white hover:text-ink transition-colors" data-testid="hero-cta">
              {s.cta} <ArrowRight size={18} />
            </Link>
            <Link to="/deals" className="inline-flex items-center gap-2 sn-glass text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/20 transition-colors">
              Today's Deals
            </Link>
          </div>
          <div className="flex gap-2 mt-10">
            {SLIDES.map((sl, k) => (
              <button key={sl.tag} onClick={() => setI(k)} className={`h-1.5 rounded-full transition-all ${k === i ? "w-8 bg-emerald" : "w-2 bg-white/30"}`} />
            ))}
          </div>
        </div>
        <div className="relative hidden lg:block">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl sn-glass p-1.5">
            <img key={s.img} src={s.img} alt={s.tag} className="h-full w-full object-cover rounded-[20px] sn-fade-up" />
          </div>
          <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl p-4 shadow-xl flex items-center gap-3 sn-fade-up">
            <span className="h-11 w-11 rounded-xl bg-emerald/10 text-emerald grid place-items-center"><Truck size={22} /></span>
            <div><div className="font-display font-bold text-ink text-sm">Same-day delivery</div><div className="text-xs text-muted-foreground">Kingston Metro · from SHOP NOW warehouses</div></div>
          </div>
        </div>
      </div>
      <div className="absolute right-6 bottom-6 hidden lg:flex gap-2">
        <button data-testid="hero-carousel-prev-btn" onClick={() => setI((v) => (v - 1 + SLIDES.length) % SLIDES.length)} className="h-10 w-10 grid place-items-center rounded-full sn-glass text-white hover:bg-white/20"><ChevronLeft /></button>
        <button data-testid="hero-carousel-next-btn" onClick={() => setI((v) => (v + 1) % SLIDES.length)} className="h-10 w-10 grid place-items-center rounded-full sn-glass text-white hover:bg-white/20"><ChevronRight /></button>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = [
    { icon: Truck, t: "Kingston to the World", s: "Delivery as fast as possible" },
    { icon: ShieldCheck, t: "Secure Checkout", s: "Card · COD · SHOP Points" },
    { icon: BadgeCheck, t: "Supporting Local Business", s: "Verified Jamaican sellers" },
    { icon: Sparkles, t: "Smart AI", s: "Anticipates what you need" },
  ];
  return (
    <div className="border-b border-border bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-4 py-5">
        {items.map((x) => (
          <div key={x.t} className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-emerald/10 text-emerald grid place-items-center shrink-0"><x.icon size={20} /></span>
            <div><div className="font-display font-semibold text-sm">{x.t}</div><div className="text-xs text-muted-foreground">{x.s}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryGrid({ cats }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-6">Shop by Category</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {cats.map((c) => {
          const Ic = ICONS[c.icon] || Sparkles;
          return (
            <Link key={c.slug} to={`/category/${c.slug}`} className="group bg-card rounded-2xl border border-border p-5 sn-card-hover">
              <span className="h-12 w-12 rounded-xl bg-ink text-white grid place-items-center group-hover:bg-emerald transition-colors mb-3"><Ic size={22} /></span>
              <div className="font-display font-semibold">{c.name}</div>
              <div className="text-xs text-muted-foreground mt-1">{c.count} products</div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

const COMING_IMG = { electronics: "https://images.unsplash.com/photo-1498049794561-7780e7231661?w=900", fashion: "https://images.unsplash.com/photo-1445205170230-053b83016050?w=900", art: "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?w=900", jewelry: "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=900", yachts: "https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=900" };
function ComingSoonRail({ cat, title }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid={`coming-soon-${cat}`}>
      <div className="relative overflow-hidden rounded-3xl bg-ink text-white min-h-[180px] flex items-end">
        <img src={COMING_IMG[cat]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" loading="lazy" />
        <div className="relative p-6 sm:p-8">
          <span className="inline-block rounded-full bg-amber-400 text-ink px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em]">Coming soon</span>
          <h2 className="font-display text-2xl sm:text-3xl font-bold mt-3">{title}</h2>
          <p className="text-white/70 text-sm mt-1 max-w-xl">Verified {title.toLowerCase()} suppliers are being onboarded. No products are listed until stock, pricing and images are confirmed. <Link to="/seller" className="underline text-amber-300">Sell {title.toLowerCase()} on SHOP NOW →</Link></p>
        </div>
      </div>
    </section>
  );
}

function FlashDeals({ deals }) {
  const [, force] = useState(0);
  useEffect(() => { const t = setInterval(() => force((v) => v + 1), 1000); return () => clearInterval(t); }, []);
  if (!deals?.length) return null;
  const allSample = deals.every((d) => d.sample);
  return (
    <section data-testid="flash-deals-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="rounded-3xl sn-ink-bg sn-noise relative overflow-hidden p-6 sm:p-8">
        <div className="flex items-center justify-between mb-6 relative">
          <div className="flex items-center gap-3 text-white">
            <span className="h-11 w-11 rounded-xl bg-coral grid place-items-center"><Zap size={22} /></span>
            <div><h2 className="font-display text-2xl font-bold">Flash & Daily Deals {allSample && <span className="ml-2 align-middle rounded-full bg-amber-400 text-ink px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider" data-testid="flash-deals-coming-soon">Coming soon</span>}</h2><p className="text-white/60 text-sm">{allSample ? "Sample deals for illustration — live deals go up once the desk approves real stock." : "Limited-time prices. Grab them before the clock runs out."}</p></div>
          </div>
          <Link to="/deals" className="hidden sm:inline-flex items-center gap-1 text-emerald font-semibold text-sm">View all <ArrowRight size={16} /></Link>
        </div>
        <div className="flex gap-4 overflow-x-auto sn-scroll-x relative pb-1">
          {deals.map((d) => {
            const tl = timeLeft(d.ends_at);
            if (d.sample) return (
              <div key={d.product.id} className="min-w-[210px] max-w-[210px] bg-white/90 rounded-2xl overflow-hidden border border-dashed border-white/40" data-testid={`flash-sample-${d.product.id}`}>
                <div className="relative aspect-square bg-slate-100">
                  <img src={d.product.image} alt={d.product.name} className="h-full w-full object-cover grayscale-[30%]" />
                  <span className="absolute top-2 left-2 bg-ink text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Sample</span>
                </div>
                <div className="p-3">
                  <div className="font-display font-semibold text-sm line-clamp-2 h-10">{d.product.name}</div>
                  <div className="mt-2 text-[11px] bg-secondary text-muted-foreground rounded-lg px-2 py-1 text-center font-semibold">Coming soon · not for sale</div>
                </div>
              </div>
            );
            return (
              <Link key={d.product.id} to={`/product/${d.product.id}`} className="min-w-[210px] max-w-[210px] bg-white rounded-2xl overflow-hidden sn-card-hover border border-transparent">
                <div className="relative aspect-square bg-slate-100">
                  <img src={d.product.image} alt={d.product.name} className="h-full w-full object-cover" />
                  <span className="absolute top-2 left-2 bg-coral text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{d.title}</span>
                </div>
                <div className="p-3">
                  <div className="font-display font-semibold text-sm line-clamp-2 h-10">{d.product.name}</div>
                  <div className="flex items-center gap-2 mt-1"><span className="font-bold text-ink">{jmd(d.product.price)}</span><span className="text-xs line-through text-muted-foreground">{jmd(d.product.prev_price)}</span></div>
                  <div className="mt-2 font-mono-sn text-xs bg-ink text-white rounded-lg px-2 py-1 text-center">
                    {tl.done ? "Ended" : `${String(tl.h).padStart(2, "0")}:${String(tl.m).padStart(2, "0")}:${String(tl.s).padStart(2, "0")}`}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function HotelStrip({ hotels }) {
  if (!hotels?.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-end justify-between mb-6">
        <div className="flex items-center gap-3"><span className="h-11 w-11 rounded-xl bg-gold/15 text-gold grid place-items-center"><Hotel size={22} /></span>
          <div><h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Hotels & Stays</h2><p className="text-sm text-muted-foreground">Book your next Jamaican escape.</p></div></div>
        <Link to="/hotels" className="text-emerald font-semibold text-sm inline-flex items-center gap-1">View all <ArrowRight size={16} /></Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {hotels.slice(0, 4).map((h) => (
          <Link key={h.slug} to={`/hotels/${h.slug}`} className="group bg-card rounded-2xl border border-border overflow-hidden sn-card-hover">
            <div className="relative aspect-[4/3] bg-slate-100"><img src={h.image} alt={h.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <span className="absolute top-3 left-3 bg-white/90 text-xs font-semibold px-2 py-1 rounded-full">{h.type}</span>
              {h.coming_soon && <span className="absolute top-3 right-3 bg-ink text-white text-[10px] font-extrabold px-2.5 py-1 rounded-full" data-testid={`home-stay-coming-soon-${h.slug}`}>COMING SOON</span>}</div>
            <div className="p-4">
              <div className="font-display font-semibold line-clamp-1">{h.name}</div>
              <div className="text-xs text-muted-foreground">{h.parish}</div>
              <div className="mt-2 text-xs font-semibold text-amber-700">{h.coming_soon ? "Bookings open once the property is verified" : <>{jmd(h.price_night)} <span className="font-normal text-muted-foreground">/ night</span></>}</div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function AppCTA() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="rounded-3xl sn-hero-mesh sn-noise relative overflow-hidden p-8 sm:p-12 grid lg:grid-cols-2 gap-8 items-center text-white">
        <div className="relative">
          <span className="inline-flex items-center gap-2 sn-glass sn-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-wider"><Smartphone size={14} /> SHOP NOW App</span>
          <h2 className="font-clash font-bold text-3xl sm:text-4xl mt-4 leading-tight">Jamaica's marketplace,<br />in your pocket.</h2>
          <p className="text-white/70 mt-4 max-w-md">Track orders in real time, book stays, redeem SHOP Points and chat with our Smart AI — delivering from Kingston to the world. Coming soon to iOS & Android.</p>
          <div className="flex flex-wrap gap-3 mt-6 items-center">
            <Link to="/get-app" data-testid="home-install-app" className="inline-flex items-center gap-2 bg-white text-ink rounded-xl px-6 py-3 font-semibold hover:bg-white/90 transition-colors"><Smartphone size={18} /> Install the App</Link>
            <div className="flex gap-3">
              <span className="sn-glass rounded-xl px-5 py-2.5 font-semibold opacity-70 leading-tight">App Store<span className="block text-[10px] font-normal text-white/60">Coming soon</span></span>
              <span className="sn-glass rounded-xl px-5 py-2.5 font-semibold opacity-70 leading-tight">Google Play<span className="block text-[10px] font-normal text-white/60">Coming soon</span></span>
            </div>
          </div>
          <Link to="/get-app" className="inline-block mt-4 text-sm text-white/80 underline hover:text-white">Or scan a QR code to install →</Link>
        </div>
        <div className="relative hidden lg:flex justify-center">
          <img src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600" alt="SHOP NOW app" className="rounded-3xl w-72 object-cover aspect-[9/16] shadow-2xl" />
        </div>
      </div>
    </section>
  );
}

const HW_TILES = [
  { mc: "Faucets Mixers and Taps", icon: "Droplets" },
  { mc: "Kitchen and Laundry", icon: "Utensils" },
  { mc: "Showers and Bathroom Fixtures", icon: "ShowerHead" },
  { mc: "Drains and Plumbing", icon: "Wrench" },
  { mc: "Power Tools", icon: "Drill" },
  { mc: "Hand Tools and Construction Tools", icon: "Hammer" },
  { mc: "Painting and Finishing Supplies", icon: "PaintRoller" },
  { mc: "Doors Locks and Security", icon: "Lock" },
  { mc: "Hinges and Furniture Hardware", icon: "DoorOpen" },
  { mc: "Electrical and General Hardware", icon: "Plug" },
  { mc: "Pumps and Water Systems", icon: "Waves" },
  { mc: "Garden and Outdoor", icon: "Trees" },
];

function HardwareTiles() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="hardware-tiles">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Shop Hardware by Category</h2>
          <p className="text-muted-foreground text-sm mt-1">Over 1,200 hardware, plumbing & home items — browse the full catalogue.</p>
        </div>
        <Link to="/category/hardware" className="text-emerald font-semibold text-sm">Shop all →</Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {HW_TILES.map((t) => {
          const Icon = HW_ICONS[t.icon] || Wrench;
          return (
            <Link key={t.mc} to={`/category/hardware?mc=${encodeURIComponent(t.mc)}`}
              data-testid={`hw-tile-${t.mc.split(" ")[0].toLowerCase()}`}
              className="group flex flex-col items-center justify-center text-center gap-2 rounded-2xl border border-border bg-card p-4 sn-card-hover">
              <span className="h-11 w-11 grid place-items-center rounded-xl bg-emerald/10 text-emerald group-hover:bg-emerald group-hover:text-white transition-colors">
                <Icon size={20} />
              </span>
              <span className="font-display font-semibold text-[13px] leading-tight line-clamp-2">{t.mc}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function FoodTile() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="food-tile">
      <Link to="/food" className="group block rounded-3xl bg-[#E11B22] text-white sn-noise relative overflow-hidden" data-testid="food-tile-link">
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center p-6 sm:p-8">
          <div>
            <span className="inline-flex items-center gap-2 bg-white/15 sn-pill px-3 py-1 text-xs font-semibold uppercase tracking-wider">Food & Dining · Jamaica-wide</span>
            <h2 className="font-clash font-bold text-2xl sm:text-3xl mt-3">KFC, jerk, Thai, fine dining — one SHOP NOW checkout</h2>
            <p className="text-white/80 text-sm mt-1 max-w-md">Order from restaurants taking orders today and discover 50+ listed restaurants across all 14 parishes.</p>
            <span className="inline-flex items-center gap-2 mt-5 bg-white text-[#E11B22] rounded-full px-5 py-2.5 text-sm font-bold group-hover:bg-ink group-hover:text-white transition-colors">Explore restaurants <ArrowRight size={16} /></span>
          </div>
          <img src="/catalogue/kfc/bucket.jpg" alt="Food & Dining" className="hidden md:block h-40 w-40 rounded-2xl object-cover" loading="lazy" />
        </div>
      </Link>
    </section>
  );
}

function SkinLabTile({ compact = false }) {
  const [shots, setShots] = useState([]);
  useEffect(() => { api.get("/products", { params: { merchant: "morgans-creek", limit: 4 } }).then(({ data }) => setShots((data.items || data || []).slice(0, 4))).catch(() => {}); }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid={compact ? "morgans-tile-compact" : "morgans-tile"}>
      <Link to="/merchant/morgans-creek" className="group block rounded-3xl bg-[#2f5a52] text-white sn-noise relative overflow-hidden" data-testid={compact ? "morgans-tile-compact-link" : "morgans-tile-link"}>
        <div className={`grid ${compact ? "md:grid-cols-[1fr_auto]" : "md:grid-cols-2"} gap-6 items-center p-6 sm:p-8`}>
          <div>
            <span className="inline-flex items-center gap-2 bg-white/15 text-[#e8e2c7] sn-pill px-3 py-1 text-xs font-semibold uppercase tracking-wider"><Sparkles size={13} /> Headline beauty partner · Jamaica-made</span>
            <h2 className="font-clash font-bold text-2xl sm:text-3xl mt-3">Morgan's Creek — Luxury Handmade Skincare</h2>
            <p className="text-white/75 text-sm mt-1 max-w-md">SPF 50 sunscreen, aromatherapy, face &amp; body scrubs, shower gels, soaks, soap bars and splashes — handmade in Jamaica since 2016.</p>
            <span className="inline-flex items-center gap-2 mt-5 bg-white text-[#2f5a52] rounded-full px-5 py-2.5 text-sm font-bold group-hover:bg-[#e8e2c7] transition-colors">Shop Morgan's Creek <ArrowRight size={16} /></span>
          </div>
          <div className={`grid ${compact ? "grid-cols-4 gap-2" : "grid-cols-4 gap-3"}`}>
            {shots.map((p, i) => (
              <img key={p.id} src={p.image} alt={p.name} loading="lazy"
                className={`${compact ? "w-16 h-16 sm:w-20 sm:h-20" : "w-full aspect-square"} object-cover rounded-2xl border border-white/10 bg-white group-hover:-translate-y-1 transition-transform`} style={{ transitionDelay: `${i * 40}ms` }} />
            ))}
          </div>
        </div>
      </Link>
    </section>
  );
}

function NewCategoriesBanner() {
  const items = [
    { slug: "cigars", label: "Premium Cigars" },
    { slug: "yachts", label: "Yachts" },
    { slug: "art", label: "Caribbean Art" },
    { slug: "jewelry", label: "Jewelry" },
    { slug: "water", label: "Water Co." },
    { slug: "marine", label: "Sea & Dive Gear" },
    { slug: "shoes", label: "Shoes" },
    { slug: "mens", label: "Men's Wear" },
    { slug: "womens", label: "Women's Wear" },
  ];
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="new-categories-banner">
      <div className="rounded-3xl bg-emerald text-white sn-noise relative overflow-hidden p-6 sm:p-8">
        <div className="relative">
          <span className="inline-flex items-center gap-2 bg-white/15 sn-pill px-3 py-1 text-xs font-semibold uppercase tracking-wider"><Sparkles size={13} /> New on SHOP NOW</span>
          <h2 className="font-clash font-bold text-2xl sm:text-3xl mt-3">9 new categories just landed.</h2>
          <p className="text-white/80 text-sm mt-1 max-w-lg">From Caribbean art and fine jewelry to premium cigars, dive gear and yachts — explore Jamaica's newest lifestyle collections.</p>
          <div className="flex flex-wrap gap-2 mt-5">
            {items.map((it) => (
              <Link key={it.slug} to={`/category/${it.slug}`} data-testid={`new-cat-${it.slug}`}
                className="bg-white/12 hover:bg-white hover:text-emerald-deep transition-colors rounded-full px-4 py-2 text-sm font-semibold">{it.label}</Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState({ cats: [], deals: [], recommended: [], jamaica: [], electronics: [], fashion: [], hotels: [], merchants: [], art: [], jewelry: [], yachts: [] });
  useEffect(() => {
    Promise.allSettled([
      api.get("/categories"), api.get("/deals"),
      api.get("/products", { params: { sort: "rating", limit: 10 } }),
      api.get("/products", { params: { category: "jamaica-made", limit: 10 } }),
      api.get("/products", { params: { category: "electronics", limit: 10 } }),
      api.get("/products", { params: { category: "fashion", limit: 10 } }),
      api.get("/hotels"), api.get("/merchants"),
      api.get("/products", { params: { category: "art", limit: 10 } }),
      api.get("/products", { params: { category: "jewelry", limit: 10 } }),
      api.get("/products", { params: { category: "yachts", limit: 10 } }),
    ]).then((rs) => {
      const [c, d, r, j, e, f, h, m, ar, jw, yc] = rs.map((x) => (x.status === "fulfilled" ? x.value.data : []));
      setData({ cats: c, deals: d, recommended: r, jamaica: j, electronics: e, fashion: f, hotels: h, merchants: m, art: ar, jewelry: jw, yachts: yc });
    });
  }, []);

  return (
    <div>
      <Hero />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-testid="export-banner"><Link to="/export" className="relative block rounded-3xl overflow-hidden min-h-[180px] bg-[#0b0b0d] text-white"><img src="/catalogue/export/containers.jpg" alt="Shipping containers" className="absolute inset-0 w-full h-full object-cover opacity-45" loading="lazy" /><div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" /><div className="relative p-6 md:p-8 max-w-xl"><div className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-amber-400">🌍 Global Export &amp; International Trade</div><div className="font-clash font-bold text-2xl md:text-4xl mt-1">SHOP NOW GLOBAL EXPORT</div><div className="text-sm text-white/80 mt-1">From Jamaica to the World — request an export quotation for Jamaican products, quotation only until carriers are configured.</div><span className="inline-block mt-3 rounded-full bg-coral px-5 py-2 text-xs font-extrabold uppercase">Explore export →</span></div></Link></section>
      <MaterialsCards />
      <TrustBar />
      <CategoryGrid cats={data.cats} />
      <ShopByDepartment />
      <FoodTile />
      <SkinLabTile />
      <ShopBySubCategory />
      <JustAdded />
      <DealOfDay />
      <NewCategoriesBanner />
      <HardwareTiles />
      <FlashDeals deals={data.deals} />
      <SkinLabTile compact />
      <RecentlyViewed title="Pick Up Where You Left Off" />
      <BecauseYouViewed />
      <ProductRail title="Recommended for You" products={data.recommended} action={<Link to="/search" className="text-emerald font-semibold text-sm">See more →</Link>} />
      <ProductRail title="Jamaica Made" products={data.jamaica} action={<Link to="/jamaica-made" className="text-emerald font-semibold text-sm">Explore →</Link>} />
      <HotelStrip hotels={data.hotels} />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="home-books-banner">
        <Link to="/books" className="group grid md:grid-cols-[1fr_320px] rounded-3xl overflow-hidden bg-ink text-white">
          <div className="p-7 sm:p-9"><span className="rounded-full bg-amber-400 text-ink px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.18em]">New · Books</span><h2 className="font-display text-2xl sm:text-3xl font-bold mt-3">Flavors of Jamaica — A Culinary Journey</h2><p className="text-white/70 text-sm mt-2 max-w-xl">Volumes 1 & 2 by Chef Curline Black & Matthew Taylor. 200+ authentic recipes with wine pairings. Pre-order your copy →</p></div>
          <img src="/catalogue/books/vol1-front-v2.jpg" alt="Flavors of Jamaica cookbook" className="h-44 md:h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        </Link>
      </section>
      <DeliveryWorldMap />
      {[
        { cat: "electronics", title: "Electronics", link: "/category/electronics", cta: "Shop all →", items: data.electronics },
        { cat: "fashion", title: "Fashion", link: "/category/fashion", cta: "Shop all →", items: data.fashion },
        { cat: "art", title: "Caribbean Art", link: "/category/art", cta: "Shop all →", items: data.art },
        { cat: "jewelry", title: "Fine Jewelry", link: "/category/jewelry", cta: "Shop all →", items: data.jewelry },
        { cat: "yachts", title: "Yachts & Cruisers", link: "/category/yachts", cta: "Explore →", items: data.yachts },
      ]
        .slice()
        .sort((a, b) => (getCatViews()[b.cat] || 0) - (getCatViews()[a.cat] || 0))
        .map((r) => r.items?.length ? (
          <ProductRail key={r.cat} title={r.title} products={r.items}
            action={<Link to={r.link} className="text-emerald font-semibold text-sm">{r.cta}</Link>} />
        ) : <ComingSoonRail key={r.cat} cat={r.cat} title={r.title} />)}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-6">Popular Merchants</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {data.merchants.map((m, mi) => (
            <Link key={`${m.slug || "m"}-${mi}`} to={`/merchant/${m.slug}`} data-testid="merchant-storefront-card" className="bg-card rounded-2xl border border-border p-4 text-center sn-card-hover">
              <img src={m.logo || "/shopnow-logo.png"} alt={m.name} className="h-16 w-16 rounded-full object-cover mx-auto mb-3" />
              <div className="font-display font-semibold text-sm line-clamp-1">{m.name}</div>
              <div className="text-xs text-muted-foreground">⭐ {m.rating} · {m.parish}</div>
            </Link>
          ))}
        </div>
      </section>

      <AppCTA />
    </div>
  );
}
